import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dumbbell, MapPin } from "lucide-react";
import { Card, CardContent } from "@/shared/components/ui/card";
import { VenueTimeBadge } from "./VenueTimeBadge";
import { venueTypeIcon } from "./venue-icons";
import type { PendingVenue } from "./types";

interface VenueQueueCardProps {
  readonly venue: PendingVenue;
  readonly onSelect: (venue: PendingVenue) => void;
}

export function VenueQueueCard({ venue, onSelect }: VenueQueueCardProps) {
  const { t } = useTranslation();
  const TypeIcon = venueTypeIcon[venue.type] ?? Dumbbell;
  const photoUrl = venue.photos[0];
  const [photoError, setPhotoError] = useState(false);

  return (
    <Card
      data-testid={`venue-row-${venue.id}`}
      className="group cursor-pointer overflow-hidden border-border/60 transition-all hover:border-wander-orange/40 hover:shadow-md"
      onClick={() => onSelect(venue)}
      role="button"
      tabIndex={0}
      aria-label={t("venues.queue.cardLabel", { name: venue.name, city: venue.city })}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(venue);
        }
      }}
    >
      <CardContent className="flex gap-4 p-4">
        <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-md bg-muted">
          {photoUrl && !photoError ? (
            <img
              src={photoUrl}
              alt={venue.name}
              className="h-full w-full object-cover"
              loading="lazy"
              onError={() => setPhotoError(true)}
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground">
              <TypeIcon className="h-6 w-6" />
              {photoError && (
                <span className="font-sans text-[10px] font-normal">
                  {t("venues.detail.photoError")}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col justify-between">
          <div>
            <div className="flex items-start justify-between gap-2">
              <h3 className="truncate font-sans text-base font-semibold text-foreground">
                {venue.name}
              </h3>
              <VenueTimeBadge venueId={venue.id} createdAt={venue.createdAt} />
            </div>

            <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate font-sans font-normal">
                {venue.city}, {venue.state}
              </span>
            </div>
          </div>

          <div className="mt-2 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 font-sans text-xs font-medium text-muted-foreground">
              <TypeIcon className="h-3 w-3" />
              {t(`venues.type.${venue.type}`)}
            </span>
            {venue.amenities.length > 0 && (
              <span className="font-sans text-xs font-normal text-muted-foreground">
                {t("venues.queue.amenitiesCount", { count: venue.amenities.length })}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
